import {lazy, FC, Suspense, useState} from 'react'
import {Route, Routes, Navigate} from 'react-router-dom'
import {MasterLayout} from '../../_metronic/layout/MasterLayout'
import {BusinessRequired} from './BusinessRequired'
import {DashboardWrapper} from '../pages/dashboard/DashboardWrapper'
import {WithChildren} from '../../_metronic/helpers'
import {AppLoading} from '../../_metronic/helpers/components/AppLoading'
import {useAuth} from '../modules/auth'
import {RegisterLayout} from '../modules/auth/RegisterLayout'
import {Registration} from '../modules/auth/components/registeration/Registration'
import {SsoBusinessRegistration} from '../modules/auth/components/registeration/SsoBusinessRegistration'
import {OnlineBusinessRegistration} from '../modules/auth/components/registeration/OnlineBusinessRegistration'
import {ONLINE_REGISTRATION_PATH} from '../modules/auth/core/onlineRegistration'
import {PageTitle} from '../../_metronic/layout/core'
import {canViewOneRowQueueInvoiceReport} from '../modules/auth/core/oneRowQueueInvoiceReportAccess'
import {canViewIsunReport} from '../modules/auth/core/isunReportAccess'
import {canViewShahrReport} from '../modules/auth/core/shahrReportAccess'
import {canUseTicketing} from '../modules/auth/core/ticketingAccess'
import {peekLoginReturn} from '../modules/auth/core/loginReturn'
import {canViewSmsLog} from '../modules/auth/core/smsLogAccess'
import {canRegisterBusiness} from '../modules/auth/components/registeration/businessRegistration'
import {isOnPremisesInstallation} from '../modules/auth/core/onPremisesAccess'
import {staffCan} from '../modules/auth/core/staffAccess'
import {getEntryPath} from '../modules/auth/core/postLoginPath'
import {needsAccountCompletion} from '../modules/auth/core/accountCompletion'
import {isOverDeviceLimit} from '../modules/auth/core/deviceLimit'
import {takeForgotPasswordRedirect} from '../modules/auth/core/credentialNotice'
import {BusinessLanding} from './BusinessLanding'

// «تکمیل حساب کاربری», «انتخاب دستگاه» and the sign-in link's «تعیین نام کاربری و رمز عبور»: pages without the
// panel's chrome.
const AccountCompletionPage = lazy(() => import('../modules/auth/completion/AccountCompletionPage'))
const DeviceLimitPage = lazy(() => import('../modules/auth/completion/DeviceLimitPage'))
const SignInLinkPage = lazy(() => import('../modules/auth/completion/SignInLinkPage'))

/**
 * Where a sign-in lands: after «رمز عبور را فراموش کرده‌اید؟» the password card (once), after a link to a new
 * ticket's request that ticket, otherwise the usual landing.
 */
const AfterSignIn: FC<{fallback: string}> = ({fallback}) => {
  const [target] = useState(() => takeForgotPasswordRedirect() || fallback)
  return <Navigate to={target} />
}

const SendWatchdogSettingsPage = lazy(
  () => import('../modules/apps/monitoring/send-watchdog/SendWatchdogSettingsPage')
)
const SendWatchdogReportPage = lazy(
  () => import('../modules/apps/monitoring/send-watchdog/SendWatchdogReportPage')
)
const SendDashboardPage = lazy(
  () => import('../modules/apps/monitoring/send-dashboard/SendDashboardPage')
)
const MonitoringCenterPage = lazy(
  () => import('../modules/apps/monitoring/center/MonitoringCenterPage')
)
const SsoLoginsPage = lazy(() => import('../modules/apps/monitoring/sso-logins/SsoLoginsPage'))
const WebsiteStatsPage = lazy(
  () => import('../modules/apps/monitoring/website-stats/WebsiteStatsPage')
)
const CallCenterPage = lazy(() => import('../modules/apps/callcenter/CallCenterPage'))
const MyBusinessesPage = lazy(() => import('../modules/apps/my-businesses/MyBusinessesPage'))
// The short links of the business services' SMS (گزارش دوره‌ای، اعلان‌های صورتحساب).
const ReportRunShortLink = lazy(() =>
  import('../modules/business-services/short-links/ShortLinks').then((module) => ({default: module.ReportRunShortLink}))
)
const InvoiceAlertShortLink = lazy(() =>
  import('../modules/business-services/short-links/ShortLinks').then((module) => ({default: module.InvoiceAlertShortLink}))
)
const InvoiceAlertsShortLink = lazy(() =>
  import('../modules/business-services/short-links/ShortLinks').then((module) => ({default: module.InvoiceAlertsShortLink}))
)
// «گزارش‌ها ← گزارش‌های دوره‌ای» and the report of one run (until 60.11.483 in «تنظیمات کسب‌وکار»).
const PeriodicReportsPage = lazy(() => import('../pages/reports/periodic/PeriodicReportsPage'))
const PeriodicReportRunPage = lazy(() =>
  import('../modules/business-services/report-run/ReportRunPage').then((module) => ({default: module.ReportRunPage}))
)

const PrivateRoutes = () => {
  const ProfilePage = lazy(() => import('../modules/profile/ProfilePage'))
  const WizardsPage = lazy(() => import('../modules/wizards/WizardsPage'))
  const WidgetsPage = lazy(() => import('../modules/widgets/WidgetsPage'))
  const InvoicesPage = lazy(() => import('../modules/apps/invoice-management/InvoicesPage'))
  const TaxWorkspacePage = lazy(() => import('../modules/apps/tax-workspace/TaxWorkspacePage'))
  const AccountingSyncPage = lazy(
    () => import('../modules/apps/accounting-sync/AccountingSyncPage')
  )
  const NotificationsPage = lazy(() => import('../modules/notifications/NotificationsPage'))
  const ProductsPage = lazy(() => import('../modules/apps/product-management/ProductsPage'))
  const EbtalsPage = lazy(() => import('../modules/apps/ebtal-management/EbtalsPage'))
  const LogsPage = lazy(() => import('../modules/apps/logs-management/LogsPage'))
  const SmsLogPage = lazy(() => import('../modules/apps/sms-log-management/SmsLogPage'))
  const WalletSystemSettingsPage = lazy(
    () => import('../modules/online-payment/admin/WalletSystemSettingsPage')
  )
  const GatewayReportPage = lazy(() => import('../modules/online-payment/admin/GatewayReportPage'))
  const ServicesMonitorPage = lazy(() => import('../modules/business-services/monitor/ServicesMonitorPage'))
  const ServicePricingPage = lazy(() => import('../modules/business-services/admin/ServicePricingPage'))
  const StuffidFileTagsPage = lazy(
    () => import('../modules/apps/stuffidFileTag-management/StuffidFileTagsPage')
  )
  const CustomersPage = lazy(() => import('../modules/apps/customer-management/CustomersPage'))
  const TaxpayersPage = lazy(() => import('../modules/apps/taxpayer-management/TaxpayersPage'))
  const TicketsPage = lazy(() => import('../modules/apps/ticket-management/TicketsPage'))
  const AdminUsersPage = lazy(() => import('../modules/apps/admin-users/AdminUsersPage'))
  const AdminPartnersPage = lazy(() => import('../modules/apps/admin-partners/AdminPartnersPage'))
  const PartnerWorkspacePage = lazy(
    () => import('../modules/apps/partner-workspace/PartnerWorkspacePage')
  )
  const SupplyChainPage = lazy(() => import('../modules/supply-chain/SupplyChainPage'))
  const InvoiceReportsPage = lazy(() => import('../pages/reports/invoice-reports/ReportsPage'))
  const IsunDailyReportPage = lazy(() => import('../pages/reports/IsunDailyReportPage'))
  const ShahrDailyReportPage = lazy(() => import('../pages/reports/ShahrDailyReportPage'))
  const OneRowQueueInvoiceReportPage = lazy(
    () => import('../pages/reports/OneRowQueueInvoiceReportPage')
  )
  const StuffIdCatalogImportPage = lazy(
    () => import('../modules/apps/stuffid-management/stuffid-import/StuffIdCatalogImportPage')
  )
  const AccountingDatabaseSettingsPage = lazy(
    () => import('../modules/apps/accounting-database/AccountingDatabaseSettingsPage')
  )
  const EbtalPage = lazy(() => import('../modules/extraService/ebtal'))
  const CompanyInqueryPage = lazy(() => import('../modules/extraService/companyInquery'))
  const {currentUser} = useAuth()
  const ticketingEnabled = canUseTicketing(currentUser)
  const isOnPremises = isOnPremisesInstallation(currentUser)

  // An account without its own mobile, name, username or password (backend 1.0.0.757+): nothing but «تکمیل حساب
  // کاربری» until they are given — the server refuses every other request anyway (ACCOUNT_INCOMPLETE). A sign-in
  // link still opens, since it sets the username and password.
  if (needsAccountCompletion(currentUser)) {
    return (
      <Routes>
        <Route
          path='auth/link'
          element={
            <SuspensedView>
              <SignInLinkPage standalone />
            </SuspensedView>
          }
        />
        <Route
          path='*'
          element={
            <SuspensedView>
              <AccountCompletionPage />
            </SuspensedView>
          }
        />
      </Routes>
    )
  }

  // Past the two devices of this kind (backend 1.0.0.757+), once the account is complete: «انتخاب دستگاه» only, until
  // the user signs one of the others out (the server answers everything else DEVICE_LIMIT).
  if (isOverDeviceLimit(currentUser)) {
    return (
      <Routes>
        <Route
          path='*'
          element={
            <SuspensedView>
              <DeviceLimitPage />
            </SuspensedView>
          }
        />
      </Routes>
    )
  }

  return (
    <Routes>
      {/*
        «کسب‌وکارهای من»: the way in for a user of more than one business, on its own page without the side menu or
        header, like the sign-in pages (the user, 2026-10-26).
      */}
      <Route
        path='my-businesses'
        element={
          <SuspensedView>
            <MyBusinessesPage />
          </SuspensedView>
        }
      />
      {/* A sign-in link opened while signed in: it sets the username and password and replaces the session. */}
      <Route
        path='auth/link'
        element={
          <SuspensedView>
            <SignInLinkPage standalone />
          </SuspensedView>
        }
      />
      {/* «ثبت موبایل» from the reminder shown while the hours given during an SMS outage last. */}
      <Route
        path='account/mobile'
        element={
          <SuspensedView>
            <AccountCompletionPage mode='mobile' />
          </SuspensedView>
        }
      />
      {/* The registration pages below stay outside this guard, in this same tree: a business made there keeps its page. */}
      <Route
        element={
          <BusinessRequired>
            <MasterLayout />
          </BusinessRequired>
        }
      >
        {/* Redirect after success login/registartion: staff land on their first granted section */}
        {/* …or, after a sign-in from a link to a new ticket's request, that ticket (loginReturn). */}
        <Route
          path='auth/*'
          element={
            <AfterSignIn
              fallback={(ticketingEnabled && peekLoginReturn()) || getEntryPath(currentUser)}
            />
          }
        />
        {/* Pages */}
        {/* A partner's business opens on «پیشخوان همکاری», every other on its dashboard (the user, 2026-10-28). */}
        <Route path='start' element={<BusinessLanding />} />
        <Route path='dashboard' element={<DashboardWrapper />} />

        {/*
          The send/inquiry watchdog and the system monitor. Registered unconditionally and guarded inside the page
          (staffCan), which says the section is not granted instead of bouncing the link to /dashboard; the backend
          still returns 403, so nothing leaks either way.
          An on-premises install has none of these pages: no «تنظیمات سیستمی» there (the user, 1405/07/25).
        */}
        {!isOnPremises && (
        <>
        <Route
          path='monitoring/send-dashboard'
          element={
            <SuspensedView>
              <PageTitle>داشبورد ارسال و استعلام</PageTitle>
              <SendDashboardPage />
            </SuspensedView>
          }
        />
        {/* کارپوشه is a cloud service: an on-premises install has no sign-in through it. */}
        {!isOnPremises && (
          <Route
            path='monitoring/sso-logins'
            element={
              <SuspensedView>
                <PageTitle>آمار ورود با کارپوشه</PageTitle>
                <SsoLoginsPage />
              </SuspensedView>
            }
          />
        )}
        {/* «آمار وب‌سایت»: visits, goals, speed, availability and SEO of asatsp.ir; guarded inside. */}
        <Route
          path='monitoring/website-stats'
          element={
            <SuspensedView>
              <PageTitle>آمار وب‌سایت</PageTitle>
              <WebsiteStatsPage />
            </SuspensedView>
          }
        />
        {/* «مرکز تماس»: the company PBX's calls from the CRM; guarded inside like the pages above. */}
        <Route
          path='callcenter/*'
          element={
            <SuspensedView>
              <PageTitle>مرکز تماس</PageTitle>
              <CallCenterPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/monitoring'
          element={
            <SuspensedView>
              <PageTitle>مانیتورینگ سامانه</PageTitle>
              <MonitoringCenterPage />
            </SuspensedView>
          }
        />
        <Route
          path='monitoring/send-watchdog'
          element={
            <SuspensedView>
              <PageTitle>تنظیمات نگهبان ارسال</PageTitle>
              <SendWatchdogSettingsPage />
            </SuspensedView>
          }
        />
        <Route
          path='monitoring/send-report'
          element={
            <SuspensedView>
              <PageTitle>گزارش ارسال و استعلام</PageTitle>
              <SendWatchdogReportPage />
            </SuspensedView>
          }
        />
        <Route
          path='monitoring/send-report/:reportDate'
          element={
            <SuspensedView>
              <PageTitle>گزارش ارسال و استعلام</PageTitle>
              <SendWatchdogReportPage />
            </SuspensedView>
          }
        />
        {/* The short path the scheduled SMS links to. */}
        <Route
          path='r/:reportDate'
          element={
            <SuspensedView>
              <PageTitle>گزارش ارسال و استعلام</PageTitle>
              <SendWatchdogReportPage />
            </SuspensedView>
          }
        />
        </>
        )}
        {/* «لینک گزارش کامل» of a periodic report's SMS, the invoice link of an alert SMS and a bundled alert SMS.
            An on-premises install has neither service, so none of their links. */}
        {!isOnPremises && (
          <>
            <Route
              path='br/:token'
              element={
                <SuspensedView>
                  <ReportRunShortLink />
                </SuspensedView>
              }
            />
            <Route
              path='tr/:token'
              element={
                <SuspensedView>
                  <InvoiceAlertShortLink />
                </SuspensedView>
              }
            />
            <Route
              path='ia/:companyId'
              element={
                <SuspensedView>
                  <InvoiceAlertsShortLink />
                </SuspensedView>
              }
            />
          </>
        )}
        <Route
          path='reports'
          element={
            <SuspensedView>
              <InvoiceReportsPage />
            </SuspensedView>
          }
        />
        {/* «پیشخوان همکاری»: guarded on the server (a partner's own users, or the system administrator
            with ?companyId=); the server is cloud-only. */}
        {!isOnPremises && (
          <Route
            path='apps/partner-workspace/*'
            element={
              <SuspensedView>
                <PartnerWorkspacePage />
              </SuspensedView>
            }
          />
        )}
        <Route
          path='reports/periodic'
          element={
            isOnPremises ? (
              <Navigate to='/reports' replace />
            ) : (
              <SuspensedView>
                <PeriodicReportsPage />
              </SuspensedView>
            )
          }
        />
        <Route
          path='reports/periodic/runs/:token'
          element={
            isOnPremises ? (
              <Navigate to='/reports' replace />
            ) : (
              <SuspensedView>
                <PeriodicReportRunPage />
              </SuspensedView>
            )
          }
        />
        <Route
          path='OneRowQueueInvoice'
          element={
            canViewOneRowQueueInvoiceReport(currentUser) ? (
              <SuspensedView>
                <OneRowQueueInvoiceReportPage />
              </SuspensedView>
            ) : (
              <Navigate to='/dashboard' replace />
            )
          }
        />
        {canViewIsunReport(currentUser) && (
          <Route
            path='reports/isun-daily'
            element={
              <SuspensedView>
                <IsunDailyReportPage />
              </SuspensedView>
            }
          />
        )}
        {canViewShahrReport(currentUser) && (
          <Route
            path='reports/shahr-daily'
            element={
              <SuspensedView>
                <ShahrDailyReportPage />
              </SuspensedView>
            }
          />
        )}
        {/* Lazy Modules */}
        <Route
          path='profile/*'
          element={
            <SuspensedView>
              <ProfilePage />
            </SuspensedView>
          }
        />
        <Route
          path='crafted/pages/wizards/*'
          element={
            <SuspensedView>
              <WizardsPage />
            </SuspensedView>
          }
        />
        <Route
          path='crafted/widgets/*'
          element={
            <SuspensedView>
              <WidgetsPage />
            </SuspensedView>
          }
        />
        {/* The theme's sample account page; anyone who still has its address lands in «حساب کاربری». */}
        <Route path='crafted/account/*' element={<Navigate to='/profile/account' replace />} />
        <Route
          path='apps/invoice-management/*'
          element={
            <SuspensedView>
              <InvoicesPage />
            </SuspensedView>
          }
        />
        <Route
          path='tax-workspace/*'
          element={
            isOnPremises ? (
              <Navigate to='/dashboard' replace />
            ) : (
              <SuspensedView>
                <TaxWorkspacePage />
              </SuspensedView>
            )
          }
        />
        <Route
          path='apps/accounting-sync/*'
          element={
            isOnPremises ? (
              <Navigate to='/dashboard' replace />
            ) : (
              <SuspensedView>
                <AccountingSyncPage />
              </SuspensedView>
            )
          }
        />
        {/* The notification centre is a cloud service; an on-premises install has none. */}
        {!isOnPremises && (
          <Route
            path='notifications/*'
            element={
              <SuspensedView>
                <NotificationsPage />
              </SuspensedView>
            }
          />
        )}
        <Route
          path='apps/customer-management/*'
          element={
            <SuspensedView>
              <CustomersPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/taxpayer-management/*'
          element={
            <SuspensedView>
              <TaxpayersPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/ticket-management/*'
          element={
            ticketingEnabled ? (
              <SuspensedView>
                <TicketsPage />
              </SuspensedView>
            ) : (
              <Navigate to='/dashboard' replace />
            )
          }
        />
        <Route
          path='apps/product-management/*'
          element={
            <SuspensedView>
              <ProductsPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/stuffidFileTag-management/*'
          element={
            isOnPremises ? (
              <Navigate to='/dashboard' replace />
            ) : (
              <SuspensedView>
                <StuffidFileTagsPage />
              </SuspensedView>
            )
          }
        />
        <Route
          path='apps/ebtal-management/*'
          element={
            <SuspensedView>
              <EbtalsPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/log-management/*'
          element={
            <SuspensedView>
              <LogsPage />
            </SuspensedView>
          }
        />
        {/* «کاربران»: guarded inside the page, like the monitoring pages above; a page of «تنظیمات سیستمی», which
            an on-premises install does not have. */}
        {!isOnPremises && (
          <>
            <Route
              path='apps/users/*'
              element={
                <SuspensedView>
                  <PageTitle>کاربران</PageTitle>
                  <AdminUsersPage />
                </SuspensedView>
              }
            />
            {/* The address of the old user list, for bookmarks. */}
            <Route path='apps/enduser-management/*' element={<Navigate to='/apps/users' replace />} />
            {/* «همکاران API»: guarded inside the page like «کاربران»; the server is cloud-only too. */}
            <Route
              path='apps/partners/*'
              element={
                <SuspensedView>
                  <PageTitle>همکاران API</PageTitle>
                  <AdminPartnersPage />
                </SuspensedView>
              }
            />
          </>
        )}
        {/* «زنجیره تأمین»: the business's page (the server answers by its service) and ASA's staff area under
            /supply-chain/admin, guarded inside; a cloud service, so not on an on-premises install. */}
        {!isOnPremises && (
          <Route
            path='supply-chain/*'
            element={
              <SuspensedView>
                <PageTitle>زنجیره تأمین</PageTitle>
                <SupplyChainPage />
              </SuspensedView>
            }
          />
        )}
        {canViewSmsLog(currentUser) && !isOnPremises && (
          <Route
            path='apps/sms-log-management/*'
            element={
              <SuspensedView>
                <SmsLogPage />
              </SuspensedView>
            }
          />
        )}
        {staffCan(currentUser, 'accountingSync') && !isOnPremises && (
          <Route
            path='apps/accounting-database-settings'
            element={
              <SuspensedView>
                <PageTitle>تنظیمات دریافت از پایگاه داده</PageTitle>
                <AccountingDatabaseSettingsPage />
              </SuspensedView>
            }
          />
        )}
        {staffCan(currentUser, 'payments') && !isOnPremises && (
          <Route
            path='apps/wallet-settings'
            element={
              <SuspensedView>
                <PageTitle>کیف پول و پرداخت آنلاین</PageTitle>
                <WalletSystemSettingsPage />
              </SuspensedView>
            }
          />
        )}
        {staffCan(currentUser, 'services') && !isOnPremises && (
          <Route
            path='apps/services-monitor'
            element={
              <SuspensedView>
                <PageTitle>مانیتور سرویس‌ها و کیف پول</PageTitle>
                <ServicesMonitorPage />
              </SuspensedView>
            }
          />
        )}
        {staffCan(currentUser, 'payments') && !isOnPremises && (
          <Route
            path='apps/payment-gateways-report'
            element={
              <SuspensedView>
                <PageTitle>گزارش درگاه‌های پرداخت</PageTitle>
                <GatewayReportPage />
              </SuspensedView>
            }
          />
        )}
        {staffCan(currentUser, 'services') && !isOnPremises && (
          <Route
            path='apps/service-pricing'
            element={
              <SuspensedView>
                <PageTitle>قیمت‌گذاری سرویس‌ها</PageTitle>
                <ServicePricingPage />
              </SuspensedView>
            }
          />
        )}
        {staffCan(currentUser, 'catalog') && !isOnPremises && (
          <Route
            path='apps/stuffid-catalog-import'
            element={
              <SuspensedView>
                <PageTitle>به‌روزرسانی شناسه‌های کالا/خدمت</PageTitle>
                <StuffIdCatalogImportPage />
              </SuspensedView>
            }
          />
        )}
        <Route
          path='extra-service/ebtal'
          element={
            <SuspensedView>
              <EbtalPage />
            </SuspensedView>
          }
        />
        <Route
          path='extra-service/companyInquery'
          element={
            <SuspensedView>
              <CompanyInqueryPage />
            </SuspensedView>
          }
        />
        {/* Page Not Found */}
        <Route path='*' element={<Navigate to='/error/404' />} />
      </Route>
      <Route element={<RegisterLayout />}>
        <Route
          path={ONLINE_REGISTRATION_PATH}
          element={
            <SuspensedView>
              <OnlineBusinessRegistration />
            </SuspensedView>
          }
        />
        <Route
          path='/taxpayer/sso-register'
          element={
            <SuspensedView>
              <SsoBusinessRegistration />
            </SuspensedView>
          }
        />
        <Route
          path='/taxpayer/register/:typ'
          element={
            <SuspensedView>
              {canRegisterBusiness(currentUser) ? (
                <Registration />
              ) : (
                <Navigate to='/dashboard' replace />
              )}
            </SuspensedView>
          }
        />
        <Route
          path='/taxpayer/register/:typ/:nid'
          element={
            <SuspensedView>
              {canRegisterBusiness(currentUser) ? (
                <Registration />
              ) : (
                <Navigate to='/dashboard' replace />
              )}
            </SuspensedView>
          }
        />
        <Route
          path='/taxpayer/register/'
          element={
            <SuspensedView>
              {canRegisterBusiness(currentUser) ? (
                <Registration />
              ) : (
                <Navigate to='/dashboard' replace />
              )}
            </SuspensedView>
          }
        />
      </Route>
    </Routes>
  )
}

const SuspensedView: FC<WithChildren> = ({children}) => {
  return (
    <Suspense fallback={<AppLoading mode='page' message='در حال بارگذاری صفحه…' />}>
      {children}
    </Suspense>
  )
}
export {PrivateRoutes, SuspensedView}
